import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import * as Phosphor from "@phosphor-icons/react";
import { Plus, PencilSimple, Trash, Eye, EyeSlash, PushPin, Star } from "@phosphor-icons/react";

const ICON_OPTIONS = [
  "Sparkle", "Bank", "Newspaper", "Calculator", "MapPin", "DeviceMobile",
  "ChartLineUp", "Buildings", "Scroll", "ShieldCheck", "Gift", "Crown",
  "Megaphone", "YoutubeLogo", "TrendUp",
];

/**
 * Admin → "Recently added" content manager. Posts here drive the Guide page's
 * "Recently added" feed (and any future "What's new" widget).
 */
export default function ProductUpdatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | item

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/product-updates");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try {
      await api.delete(`/admin/product-updates/${it.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const togglePinned = async (it) => {
    try {
      await api.put(`/admin/product-updates/${it.id}`, {
        title: it.title,
        body: it.body,
        icon: it.icon,
        link: it.link,
        pinned: !it.pinned,
        published: it.published,
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const togglePublished = async (it) => {
    try {
      await api.put(`/admin/product-updates/${it.id}`, {
        title: it.title,
        body: it.body,
        icon: it.icon,
        link: it.link,
        pinned: it.pinned,
        published: !it.published,
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-product-updates">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Star size={20} weight="duotone" className="text-[hsl(var(--secondary))]" />
            <h2 className="font-serif text-2xl">Recently added (Guide feed)</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Cards that appear under "Recently added" on the user Guide page. Pinned items show first.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="add-update-btn">
          <Plus size={14} weight="bold" /> Add update
        </button>
      </div>

      {loading ? (
        <div className="card-flat p-8 text-center text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-flat p-12 text-center">
          <Star size={32} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">No updates yet. Users see a hard-coded fallback list until you publish one.</p>
          <button onClick={() => setEditing("new")} className="btn-primary px-4 py-2 text-sm">Publish your first update</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((it) => {
            const Icon = Phosphor[it.icon] || Phosphor.Sparkle;
            return (
              <div key={it.id} className="card-flat p-5" data-testid={`update-card-${it.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="h-9 w-9 border hairline flex items-center justify-center text-[hsl(var(--secondary))] shrink-0">
                    <Icon size={16} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-serif text-lg">{it.title}</div>
                      {it.pinned && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[hsl(var(--secondary))]/60 text-[hsl(var(--secondary))]">Pinned</span>}
                      {!it.published && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-yellow-600/90 text-black">Hidden</span>}
                    </div>
                    {it.link && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{it.link}</div>}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-3">{it.body}</p>
                <div className="flex justify-end gap-1 pt-2 border-t hairline">
                  <button onClick={() => togglePinned(it)} className="btn-ghost p-2" title={it.pinned ? "Unpin" : "Pin"} data-testid={`update-pin-${it.id}`}>
                    <PushPin size={14} weight={it.pinned ? "fill" : "regular"} />
                  </button>
                  <button onClick={() => togglePublished(it)} className="btn-ghost p-2" title={it.published ? "Hide" : "Show"} data-testid={`update-show-${it.id}`}>
                    {it.published ? <Eye size={14} /> : <EyeSlash size={14} />}
                  </button>
                  <button onClick={() => setEditing(it)} className="btn-ghost p-2" title="Edit" data-testid={`update-edit-${it.id}`}>
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => remove(it)} className="btn-ghost p-2 hover:text-destructive" title="Delete" data-testid={`update-delete-${it.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <UpdateEditModal
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function UpdateEditModal({ item, onClose, onSaved }) {
  const isNew = !item;
  const [form, setForm] = useState({
    title: item?.title || "",
    body: item?.body || "",
    icon: item?.icon || "Sparkle",
    link: item?.link || "",
    pinned: item?.pinned ?? false,
    published: item?.published ?? true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/product-updates", form);
        toast.success("Published");
      } else {
        await api.put(`/admin/product-updates/${item.id}`, form);
        toast.success("Saved");
      }
      onSaved();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const Preview = Phosphor[form.icon] || Phosphor.Sparkle;
  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4" data-testid="update-edit-modal">
      <button className="absolute inset-0 bg-black/70" onClick={onClose} aria-label="Close" />
      <form onSubmit={submit} className="relative card-flat w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="eyebrow">{isNew ? "Add update" : "Edit update"}</div>
        <label className="block">
          <span className="eyebrow block mb-1.5">Title *</span>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark w-full px-3 py-2.5 text-sm" maxLength={140} required data-testid="update-title-input" />
        </label>
        <label className="block">
          <span className="eyebrow block mb-1.5">Body *</span>
          <textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-dark w-full px-3 py-2 text-sm" maxLength={600} required data-testid="update-body-input" />
          <div className="text-[10px] text-muted-foreground mt-1">{form.body.length}/600</div>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="eyebrow block mb-1.5">Icon</span>
            <div className="flex items-center gap-2">
              <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="input-dark w-full px-3 py-2 text-xs" data-testid="update-icon-input">
                {ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="h-9 w-9 border hairline flex items-center justify-center text-[hsl(var(--secondary))]">
                <Preview size={16} weight="duotone" />
              </div>
            </div>
          </label>
          <label className="block">
            <span className="eyebrow block mb-1.5">Link (optional)</span>
            <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="input-dark w-full px-3 py-2 text-xs font-mono" placeholder="/app/loans" data-testid="update-link-input" />
          </label>
        </div>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="update-pinned-input" /> Pinned (shows first)</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="update-published-input" /> Published</label>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary px-4 py-2 text-sm" data-testid="update-save-btn">
            {saving ? "Saving…" : isNew ? "Publish" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
